import React, { useEffect } from "react";
import { GuidedAnalysisFlow } from "../models";
import { useAssist } from "../../../context/AssistContext";

interface GuidedAnalysisViewerProps {
  flow: GuidedAnalysisFlow;
}

export const GuidedAnalysisViewer: React.FC<GuidedAnalysisViewerProps> = ({ flow }) => {
  const {
    state,
    startGuidedAnalysis,
    setGuidedAnalysisAnswer,
    goToNextGuidedStep,
    goToPreviousGuidedStep,
  } = useAssist();

  const active = state.activeGuidedAnalysis?.flowId === flow.id ? state.activeGuidedAnalysis : undefined;

  useEffect(() => {
    if (!active) startGuidedAnalysis(flow);
  }, [active, startGuidedAnalysis, flow]);

  if (!active) {
    return (
      <div className="mm-surface p-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">Chargement de l'analyse...</p>
      </div>
    );
  }

  const step = flow.steps[active.currentStepIndex];
  const isLast = active.currentStepIndex === flow.steps.length - 1;
  const isFirst = active.currentStepIndex === 0;

  return (
    <div className="mm-surface p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Analyse guidée</p>
          <h3 className="text-lg font-semibold">{flow.title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">{flow.description}</p>
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Étape {active.currentStepIndex + 1}/{flow.steps.length}
        </span>
      </div>

      <div className="space-y-2">
        <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{step.title}</p>
        <p className="text-sm text-slate-700 dark:text-slate-200">{step.question}</p>
        {step.helperText && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{step.helperText}</p>
        )}
        <textarea
          value={active.answers[step.id] || ""}
          onChange={(e) => setGuidedAnalysisAnswer(step.id, e.target.value)}
          className="w-full min-h-[120px] rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 text-sm p-3 text-slate-800 dark:text-slate-100"
          placeholder="Note ta réponse ici..."
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => goToPreviousGuidedStep(flow)}
          disabled={isFirst}
          className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 text-sm disabled:opacity-60"
        >
          Précédent
        </button>
        <button
          type="button"
          onClick={() => goToNextGuidedStep(flow)}
          className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
        >
          {isLast ? "Terminer" : "Étape suivante"}
        </button>
      </div>

      {isLast && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900 space-y-2">
          <p className="text-sm font-semibold">Résumé rapide (local)</p>
          <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1">
            {flow.steps.map((s) => (
              <li key={s.id}>
                <span className="font-semibold">{s.title}:</span>{" "}
                <span>{active.answers[s.id] || "(non renseigné)"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

